import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import i18n from 'i18next'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import SlideActionsButton from './slide-actions-button'
import { HandleConfirmPrompt } from './slide-panel-types'
import { getSlideLabel, SlideViewMode } from './slide-view'

type SortableSlideProps = {
  id: string
  currentId: number
  index: number
  thumbnail: string
  slideDescription?: string
  viewMode: SlideViewMode
  handleConfirmPrompt: HandleConfirmPrompt
  onSelectClick: (id: number | string, isShiftKey?: boolean) => void
  selectedSlideIds: number[]
}

let currentUrlParams = new URLSearchParams(window.location.search)

const SortableSlide = ({
  id,
  currentId,
  index,
  thumbnail,
  slideDescription,
  viewMode,
  handleConfirmPrompt,
  onSelectClick,
  selectedSlideIds
}: SortableSlideProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, node } = useSortable({ id })
  const slideId = Number(id)
  const history = useHistory()
  const isNamesView = viewMode === 'names'
  const isCurrent = currentId === slideId
  const isSelected = selectedSlideIds.indexOf(slideId) !== -1
  const slideLabel = getSlideLabel(slideDescription, index, number =>
    String(i18n.t('editor.left-panel.slides.slide-number', { number }))
  )

  const onClick = (slideId: number) => {
    currentUrlParams.set('slide', slideId.toString())
    history.push(window.location.pathname + '?' + currentUrlParams.toString())
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const hasScrolledToCurrentSlide = useRef(false)

  useEffect(() => {
    if (hasScrolledToCurrentSlide.current || id !== currentId?.toString() || index < 3) return

    const item = node.current
    const view = item?.closest('.slides')?.parentElement

    if (!item || !view) return

    hasScrolledToCurrentSlide.current = true

    const itemRect = item.getBoundingClientRect()
    const viewRect = view.getBoundingClientRect()

    if (itemRect.top < viewRect.top) {
      view.scrollTop += itemRect.top - viewRect.top
    } else if (itemRect.bottom > viewRect.bottom) {
      view.scrollTop += itemRect.bottom - viewRect.bottom
    }
  }, [currentId, id, index, node])

  return (
    <div id="sortableSlide" ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className={`slide-container ${isNamesView ? 'slide-container-names' : ''}`}
        id="sortableSlide-slide-container"
        onClick={e => {
          if ((e.target as HTMLElement).closest('.slide-actions-trigger, .indico-dropdown-content'))
            return
          if (!e.ctrlKey && !e.shiftKey) {
            onClick(slideId)
          } else if (e.ctrlKey) {
            onSelectClick(slideId)
          } else if (e.shiftKey) {
            onSelectClick(slideId, true)
          }
        }}
      >
        {isNamesView ? (
          <div
            className={[
              'slide-name-row',
              isCurrent ? 'slide-name-row-current' : '',
              isSelected ? 'slide-name-row-selected' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            aria-current={isCurrent ? 'true' : undefined}
          >
            <span className="slide-name-label" title={slideLabel}>
              {slideLabel}
            </span>
            <SlideActionsButton
              slideId={slideId}
              handleConfirmPrompt={handleConfirmPrompt}
              selectedSlideIds={selectedSlideIds}
              hasDescription={Boolean(slideDescription?.trim())}
            />
          </div>
        ) : (
          <>
            <span
              id="sortableSlide-slide-container-index"
              className={`slide-index ${isCurrent ? 'slide-index-selected' : ''}`}
            >
              {index + 1}
            </span>
            <Tooltip disableHoverableContent>
              <TooltipTrigger asChild>
                <div
                  id="sortableSlide-slide-container-selected"
                  className={`slide ${isSelected ? 'slide-container-selected' : ''}`}
                >
                  <img
                    id="sortableSlide-slide-container-selected-thumbnail"
                    src={thumbnail}
                    alt={slideLabel}
                    style={{ objectFit: 'contain', width: 150, height: 84 }}
                  />
                  {slideDescription && <div className="slide-description">{slideDescription}</div>}
                  <SlideActionsButton
                    slideId={slideId}
                    handleConfirmPrompt={handleConfirmPrompt}
                    selectedSlideIds={selectedSlideIds}
                    hasDescription={Boolean(slideDescription?.trim())}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="slide-thumbnail-tooltip">
                {slideDescription ? (
                  slideDescription
                ) : (
                  <strong>{i18n.t('editor.left-panel.slides.name')}</strong>
                )}
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  )
}

export default SortableSlide
